import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  LayoutDashboard,
  CheckSquare,
  AlertTriangle,
  AlertCircle,
  Users,
  Server,
  Folder,
  BarChart3,
  Settings,
  Shield,
  ClipboardList,
  FileText,
} from 'lucide-react';

const navigationItems = [
  { title: 'Overview', url: '/dashboard', icon: LayoutDashboard },
  { title: 'Assets', url: '/assets', icon: Server },
  { title: 'Tasks', url: '/tasks', icon: ClipboardList },
  { title: 'Controls', url: '/controls', icon: CheckSquare },
  { title: 'Risks', url: '/risks', icon: AlertTriangle },
  { title: 'Incidents', url: '/incidents', icon: AlertCircle },
  { title: 'Suppliers', url: '/suppliers', icon: Users },
  { title: 'Evidence', url: '/evidence', icon: Folder },
  { title: 'Reports', url: '/reports', icon: BarChart3 },
  { title: 'Standards', url: '/standards', icon: FileText },
  { title: 'Security', url: '/security', icon: Shield },
  { title: 'Settings', url: '/settings', icon: Settings },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const { profile, organization } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;

  const isActive = (path: string) => currentPath === path;

  // Filter navigation based on user role
  const filteredNavigation = navigationItems.filter(item => {
    if (profile?.role === 'viewer') {
      return !['Settings'].includes(item.title);
    }
    return true;
  });

  return (
    <Sidebar className={state === 'collapsed' ? "w-16" : "w-64"}>
      <SidebarContent className="px-2 pt-4">
        <SidebarGroup>
          <SidebarGroupLabel className="text-base font-semibold text-sidebar-foreground px-3 py-4 mb-2">
            {organization?.name || 'ComplyManager'}
          </SidebarGroupLabel>
          
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {filteredNavigation.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                    <NavLink 
                      to={item.url} 
                      end
                      className="flex items-center gap-4 text-sidebar-foreground hover:text-sidebar-accent-foreground hover:bg-sidebar-accent/50 text-base py-3 px-3 rounded-md transition-colors"
                    >
                      <item.icon className="h-6 w-6 flex-shrink-0" />
                      {state !== 'collapsed' && <span className="text-[15px] font-medium">{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter className="mt-auto border-t border-sidebar-border">
        <div className="px-4 py-5 text-sm text-muted-foreground">
          © {new Date().getFullYear()} Autelion AB
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}